import { AddFieldDef } from "../utils/AddFieldDef";
import { appstr } from "../utils/appstr";
import {
  enumCalcCode,
  enumCellObjectSubtypes,
  enumFieldType,
  enumHierarchyTypes,
  enumObjClassNames,
  kDesiredHolders,
  kEmployeeID,
  kEmployeePositionID,
  kParentPositionID,
  kPersonID,
  kPositionHoldersCount,
  kPositionAllocation,
  kCurrentPositionCost,
  kPositionID,
  kPositionTitleID,
  kSupervisorID,
  kTitleID,
  objTypeClassMap,
  posObjClassFieldIds,
  kPositionCountFields,
  kPositionFieldIDs,
  kNameID,
  kEmployeeLocation,
  kEmployeeSalary,
  kEmployeePay,
  kEmployeeCostCenter,
  enumObjectTypes
} from "../constants";
import { QGenericIndex } from "./QGenericIndex";
import { QObjDBFieldDef } from "./QObjDBFieldDef";
import { QObjDBObject } from "./QObjDBObject";
import { QObjMgr } from "./QObjMgr";
import { QXMLObject } from "./QXMLObject";
import { ReportTiming } from "../utils/ReportTiming";
import { Globals } from "./Globals";
import { AddCalcField } from "../utils/AddCalcField";

/**
 * @typedef {import('./QObjDB').QObjDB} QObjDB
 * @typedef {import('./QObjDBObject').QObjDBObject} QObjDBObject
 * @typedef {import('./QXWSMgr').QXWSMgr} QXWSMgr
 */

export class QObjDBClass extends QXMLObject {
  constructor() {
    super();
    /**
     * @private
     * @type {(null | QObjDB)}
     */
    this.fObjDB = null;

    /**
     * @type {(null | string)}
     */
    this.fClassName = null;
    this.fFieldList = null;
    this.fObjMgr = null;

    this.fFieldNameIndex = null;
    this.fFieldIDIndex = null;
    this.fDefUID = 0;
  }

  /**
   *
   * @param {QObjDBClass | QObjDBObject} override
   * @returns {QObjDBObject | null}
   */
  GetBaseRecord(override) {
    const refID = override.SmartGetFieldValue("RefID");

    const objDB = this.GetObjDB();
    let objType = override.SmartGetFieldValue("objType");
    if (!objType) objType = enumObjectTypes.holder;
    const className = objTypeClassMap[objType];

    const objClass = objDB.GetClassByName(className);
    const recordIdFieldDef = objClass.GetRecordIDFieldDef();

    return objClass.FindObject(recordIdFieldDef, refID, false);
  }

  RestoreDefaultDocFields() {
    this.AddReqFields();
    this.AddCalcDefs();
  }

  //====================================================================
  LoadDynPositions(positionData, holderClass) {
    var positionList = [];

    // Pre-build holder index: O(N) single scan instead of O(P×N) nested loops
    var holderMap = new Map();
    var empPosFieldDef = holderClass.GetFieldDefByID("Employee PositionID");
    var objCount = holderClass.GetObjectCount();
    for (var h = 0; h < objCount; h++) {
      var anObj = holderClass.GetIndexObject(h);
      var posID = empPosFieldDef ? anObj.GetFieldValue(empPosFieldDef) : null;
      if (posID) {
        if (!holderMap.has(posID)) holderMap.set(posID, []);
        holderMap.get(posID).push(anObj);
      }
    }

    // Hoist invariant lookups outside the loop
    var posIDDef = this.GetRecordIDFieldDef();
    var fieldName = posIDDef.GetFieldName();

    for (var i = 0; i < positionData.length; i++) {
      var positionID = positionData[i][fieldName];
      var newObj = this.AddNewObject();
      newObj.SetHolderList(holderMap.get(positionID) || []);
      positionList.push(newObj);
      this.ProcessObject(newObj, positionData[i]);
    }
    return positionList;
  }

  LoadDynObjects(chartdata) {
    var objList = [];
    var objMgr = this.fObjMgr;
    ReportTiming("Starting Creating DBObjs");
    for (var i = 0; i < chartdata.length; i++) {
      var newObj = new QObjDBObject();
      newObj.InitNewObject(this);
      objMgr.BulkInsertObject(newObj);
      this.Journal(newObj, true);
      this.ProcessObject(newObj, chartdata[i]);
      objList.push(newObj);
    }
    objMgr.FinalizeBulkInsert();
    ReportTiming("Done Creating DBObjs");
    return objList;
  }

  ProcessObject(newObj, jsonData) {
    for (var field in this.fFieldList) {
      var thisField = this.fFieldList[field];
      var fieldName = thisField.GetFieldName();
      var fieldValue = jsonData[fieldName];
      if (fieldValue != null || thisField.GetCalcCode() === enumCalcCode.customcalc) {
        newObj.AddNewFieldValue(thisField, fieldValue);
      }
    }
  }

  WireObjects(objList, recordIDIndex) {
    var objDB = this.GetObjDB();

    for (var i = 0; i < objList.length; i++) {
      var theObj = objList[i];
      var supervisor = theObj.GetObjectSupervisorID();
      var parentObj = recordIDIndex.FindObject(supervisor);
      //if (theObj != topObj && parentObj != topObj)
      //    continue //Handling move overrides.
      if (!objDB.CheckforCircularReference(theObj, parentObj)) theObj.SetParent(parentObj);
    }
  }

  /**
   *
   * @param {string} className
   * @param {boolean=} loadFromStorage
   * @param {QXWSMgr=} wsMgr
   * @param {[QObjDBObject]=} overrides
   * @returns
   */
  InitClass(className, loadFromStorage, wsMgr = null, overrides, objDB = null) {
    if (objDB) this.SetObjDB(objDB);
    this.fClassName = className;
    this.SetXMLElementName("qclass");
    this.fFieldList = [];
    this.fWSMGr = wsMgr;
    this.fObjMgr = new QObjMgr();
    this.fObjMgr.InitObjMgr(this);

    if (overrides) {
      if (!loadFromStorage) this.AddOverrideDefs();
      return;
    }

    if (!loadFromStorage) this.AddReqFields();

    this.AddCalcDefs();
  }

  InitClassLightweight(className, objDB = null) {
    if (objDB) this.SetObjDB(objDB);
    this.fClassName = className;
    this.fFieldList = [];

    this.fObjMgr = new QObjMgr();
    this.fObjMgr.InitObjMgr(this);

    this.AddOverrideDefs();
  }

  AddOverrideDefs() {
    //Currently only used for overrides to box DB objects. This function would need to be more lightweight for further generalization
    AddFieldDef(this, "ID", "ID", "ID", true);
    AddFieldDef(this, "Type", "Type", "Type", false);
    AddFieldDef(this, "objType", "objType", "objType", true);
    AddFieldDef(this, "Comment", "Comment", "Comment", true);
    AddFieldDef(this, "RefID", "RefID", "RefID", true);
    AddFieldDef(this, "RefObj", "RefObj", "RefObj", true);
    AddFieldDef(this, "override_id", "override_id", "override_id", true);
    AddFieldDef(this, "ParentID", "ParentID", "ParentID", true);
    AddFieldDef(this, "displayObject", "displayObject", "displayObject", true);
    AddFieldDef(this, "fromOutsidePlan", "fromOutsidePlan", "fromOutsidePlan", true);
  }

  GetDocRef() {
    return this.fObjDB?.GetDocRef() ?? null;
  }

  IsCaseSensitiveFields() {
    const version = this.GetDocRef()?.GetVersion();
    return !!(version && version >= 3);
  }

  IsUsingFieldCarets() {
    const version = this.GetDocRef()?.GetVersion();
    return !version || version <= 2;
  }

  GetNextUID() {
    if (this.fObjDB) this.fObjDB.GenerateGlobalUID(this);
    else this.fDefUID++;
    return this.fDefUID;
  }

  ClearFormulaCache() {
    var fC = this.GetFieldCount();
    for (var i = 0; i < fC; i++) {
      var f = this.GetIndexFieldDef(i);
      f.ClearFormulaCache();
    }
  }

  GetPositionHolders(positionID) {
    var holders = [];
    var objCount = this.GetObjectCount();
    for (
      var i = 0;
      i < objCount;
      i++ // should be using an index here.
    ) {
      var anObj = this.GetIndexObject(i);
      if (anObj.SmartGetFieldValue("Employee PositionID") === positionID) {
        holders.push(anObj);
      }
    }
    return holders;
  }

  // not really a fan of this, should be a better way of doing this
  GetUniquePositionIDs() {
    var positionIDs = [];
    var objCount = this.GetObjectCount();
    for (
      var i = 0;
      i < objCount;
      i++ // should be using an index here.
    ) {
      var anObj = this.GetIndexObject(i);
      var employeePositionID = anObj.SmartGetFieldValue("Employee PositionID");
      if (positionIDs.indexOf(employeePositionID) === -1) {
        positionIDs.push(employeePositionID);
      }
    }
    return positionIDs;
  }

  Journal(anObj, newObj) {
    this.ClearFormulaCache();
    this.fObjDB.Journal(anObj, newObj);
  }

  JournalObjList() {
    this.fObjDB.JournalObjList(this);
  }

  AddReqFields() {
    // Always add an ID field
    AddFieldDef(this, "ID", "ID", "ID", true);

    this.AddOrphanField();
    this.AddTopSubChartField();
    this.AddLockedField();
    this.AddPersistField();
  }

  AddLockedField() {
    AddFieldDef(this, "Locked", "Locked", appstr("Locked"), false);
  }

  AddPersistField() {
    AddFieldDef(this, "Persist", "Persist", appstr("Persist"), false);
  }

  AddOrphanField() {
    AddFieldDef(this, "Orphan", "Orphan", appstr("Orphan"), true);
  }

  AddTopSubChartField() {
    AddFieldDef(this, "TopOfSubChart", "Top Of SubChart", appstr("TopOfSubChart"), true);
  }

  GetRecordIDFieldDef() {
    const curDoc = this.GetDocRef();
    if (!curDoc.IsPositionDocument()) {
      return this.GetFieldDefByID(kPersonID);
    }

    if (this.GetClassName() === enumObjClassNames.box) {
      return this.GetFieldDefByID(kEmployeeID);
    } else {
      return this.GetFieldDefByID(kPositionID);
    }
  }

  GetTitleFieldDef() {
    const curDoc = this.GetDocRef();
    if (!curDoc.IsPositionDocument()) {
      return this.GetFieldDefByID(kTitleID);
    }
    if (this.GetClassName() === enumObjClassNames.box) {
      return this.GetFieldDefByID(kTitleID);
    } else {
      return this.GetFieldDefByID(kPositionTitleID);
    }
  }

  /**
   * The value of this field drives the hierarchy of the chart
   *
   * In Employee charts, this is the relationship between the employee and their supervisor
   *
   * In Position charts, this is the relationship between the position and its parent position
   * In Position charts, employees do not have a supervisor
   */
  GetSupervisorIDFieldDef() {
    const curDoc = this.GetDocRef();
    if (!curDoc.IsPositionDocument()) {
      return this.GetFieldDefByID(kSupervisorID);
    }

    if (this.GetClassName() === enumObjClassNames.box) {
      return null;
    } else {
      return this.GetFieldDefByID(kParentPositionID);
    }
  }

  /**
   * The value of this field drives the placement of a given object in the chart
   *
   * This differs from supervisor relationships for employees in Position charts
   * In this case, the parent of the employee is the position they are assigned to
   */
  GetParentIDFieldDef() {
    const curDoc = this.GetDocRef();

    if (curDoc.IsPositionDocument() && this.GetClassName() === enumObjClassNames.box) {
      return this.GetFieldDefByID(kEmployeePositionID);
    }

    return this.GetSupervisorIDFieldDef();
  }

  AddCalcDefs() {
    const className = this.GetClassName();
    const isPositionClass = className === enumObjClassNames.position;

    AddCalcField("Level", "Level", appstr("Level"), enumCalcCode.level, this);
    AddCalcField(
      "SubChartLevel",
      "SubChart Level",
      appstr("SubChartLevel"),
      enumCalcCode.subchartlevel,
      this
    );
    AddCalcField(
      "SpanOfControl",
      "Span Of Control",
      appstr("SpanOfControl"),
      enumCalcCode.spanofcontrol,
      this
    );
    AddCalcField(
      "Subordinates",
      "Subordinates",
      appstr("Subordinates"),
      enumCalcCode.subordinates,
      this
    );
    AddCalcField("Headcount", "Headcount", appstr("Headcount"), enumCalcCode.headcount, this);

    // Position-specific count fields - only for position class
    if (isPositionClass) {
      AddCalcField(
        "PositionCount",
        "Position Count",
        appstr("PositionCount"),
        enumCalcCode.positioncount,
        this
      );
      AddCalcField(
        "SubordinatePositions",
        "Subordinate Positions",
        appstr("SubordinatePositions"),
        enumCalcCode.subordinatepositions,
        this
      );
      AddCalcField(
        "DirectSubordinatePositions",
        "Direct Subordinate Positions",
        appstr("DirectSubordinatePositions"),
        enumCalcCode.directsubordinatepositions,
        this
      );
      AddCalcField(
        "PeerPositions",
        "Peer Positions",
        appstr("PeerPositions"),
        enumCalcCode.peerpositions,
        this
      );
      AddCalcField(
        "BudgetedHeadcount",
        "Budgeted Headcount",
        appstr("BudgetedHeadcount"),
        enumCalcCode.static,
        this
      );
      AddCalcField(
        "SalaryRollup",
        "Salary Rollup",
        appstr("SalaryRollup"),
        enumCalcCode.static,
        this
      );
      AddCalcField(
        "CurrentPositionCost",
        kCurrentPositionCost,
        appstr("CurrentPositionCost"),
        enumCalcCode.static,
        this
      );
      AddCalcField(
        posObjClassFieldIds.HOLDER_COUNT,
        kPositionHoldersCount,
        appstr(kPositionHoldersCount),
        enumCalcCode.holderCount,
        this
      );
      AddCalcField(
        kPositionAllocation,
        kPositionAllocation,
        appstr(kPositionAllocation),
        enumCalcCode.static,
        this
      );
      AddCalcField(
        "PositionBudgetRollup",
        "Position Budget Rollup",
        appstr("PositionBudgetRollup"),
        enumCalcCode.static,
        this
      );
    }
    AddCalcField(
      "ReportsToName",
      "ReportsToName",
      appstr("ReportsToName"),
      enumCalcCode.reportstoname,
      this
    );
    AddCalcField("Peers", "Peers", appstr("Peers"), enumCalcCode.peers, this);
    AddCalcField(
      "NewSuperID",
      "SuperIDLookup",
      appstr("NewSuperID"),
      enumCalcCode.newsupervisorID,
      this
    );
  }

  AddNineBox() {
    var fieldDef = AddCalcField(
      "NineBox",
      "NineBox",
      appstr("ninebox"),
      enumCalcCode.ninebox,
      this
    );
    fieldDef.SetFieldType(enumFieldType.image);
  }

  AddART() {
    var fieldStr = "Also Reports To";
    var aField = new QObjDBFieldDef();
    aField.InitField(fieldStr, fieldStr, this);
    aField.SetFieldType(enumFieldType.object);
    aField.SetFieldSubtype(enumCellObjectSubtypes.alsoreportsto);
    aField.SetReadOnly(true);
    aField.SetFieldLabel(appstr(fieldStr));
    this.AddField(aField);
  }

  AddAllocationRatio() {
    AddCalcField(
      "Allocation Ratio",
      "Allocation Ratio",
      appstr("Allocation Ratio"),
      enumCalcCode.ratio,
      this
    );
  }

  /**
   * Check if AuxSuperID field has mapping in metadata fields
   * @param {Record<string, string>} fieldMapping - Mapping of field IDs from metadata fields
   * @returns {boolean}
   */
  ActiveAuxMapping(fieldMapping) {
    var n = this.GetFieldCount();
    for (var i = 0; i < n; i++) {
      var aFieldDef = this.GetIndexFieldDef(i);
      var fieldId = aFieldDef.GetFieldID();
      if (fieldId.includes("AuxSuperID") && fieldMapping?.[fieldId]) return true;
    }
    return false;
  }

  HasAllocationRatioFields() {
    const requiredFields = [posObjClassFieldIds.HOLDER_COUNT, kDesiredHolders];
    return requiredFields.every(field =>
      this.fFieldList.some(aFieldDef => aFieldDef.GetFieldID().includes(field))
    );
  }

  GetClassName() {
    return this.fClassName;
  }

  GetObjMgr() {
    return this.fObjMgr;
  }

  GetRefLib() {
    return this.fObjMgr.GetRefLib();
  }

  GenUID() {
    return this.fObjDB.GenUID();
  }

  SetObjDB(theObjDB) {
    this.fObjDB = theObjDB;
  }

  GetObjDB() {
    return this.fObjDB;
  }

  GetSubChartContext() {
    return this.fObjDB.GetSubChartContext();
  }

  ResetIndex() {
    this.fFieldNameIndex = null;
    this.fFieldIDIndex = null;
    this._fieldDefCache = null;

    this.fObjMgr.ResetFieldIndex();
  }

  AddField(theField) {
    //	theField.SetParentClass(this);
    this.fFieldList.push(theField);
    this.ResetIndex();
  }

  GetFieldCount() {
    return this.fFieldList.length;
  }

  GetIndexFieldDef(theIndex) {
    return this.fFieldList[theIndex];
  }

  BuildFieldNameIndex() {
    var nameIndex = new QGenericIndex();
    nameIndex.InitIndex();
    const caseSensitive = this.IsCaseSensitiveFields();
    var n = this.GetFieldCount();
    for (var i = 0; i < n; i++) {
      var fieldDef = this.fFieldList[i];
      var fieldName = fieldDef.GetFieldName();
      if (!caseSensitive) fieldName = fieldName.toLowerCase();
      nameIndex.AddToIndex(fieldDef, fieldName);
    }
    this.fFieldNameIndex = nameIndex;
  }

  /**
   * @param {string} term
   * @param {Object} [options]
   * @param {boolean} [options.doIdLookup]
   * @param {boolean} [options.doNameLookup]
   * @param {boolean} [options.doKeywordLookup]
   * @returns {(QObjDBFieldDef | null)}
   */
  LookupFieldDef(term, options) {
    const doIdLookup = options?.doIdLookup ?? true;
    const doNameLookup = options?.doNameLookup ?? true;
    const doKeywordLookup = options?.doKeywordLookup ?? true;

    let fieldDef = null;
    if (doIdLookup) {
      fieldDef = this.GetFieldDefByID(term);
    }
    if (!fieldDef && doNameLookup) {
      fieldDef = this.GetFieldDefByName(term);
    }
    if (!fieldDef && doNameLookup) {
      fieldDef = this.GetFieldDefByName(term.replaceAll(" ", "^"));
    }
    if (!fieldDef && doKeywordLookup) {
      fieldDef = this.GetFieldDefByKeyWord(term);
    }

    return fieldDef;
  }

  /**
   * @param {string} fieldName
   * @returns {QObjDBFieldDef | null}
   */
  GetFieldDefByName(fieldName) {
    // Performance
    if (this.fFieldNameIndex == null) this.BuildFieldNameIndex();

    if (!fieldName || typeof fieldName !== "string") {
      return null;
    }

    const caseSensitive = this.IsCaseSensitiveFields();
    if (!caseSensitive) fieldName = fieldName.toLowerCase();
    var fieldDef = this.fFieldNameIndex.FindObject(fieldName);

    return fieldDef;
  }

  GetFieldDefByLabel(fieldLabel) {
    // Performance
    for (var aFieldIdx in this.fFieldList) {
      var theFieldDef = this.fFieldList[aFieldIdx];
      if (
        theFieldDef.GetFieldLabel() == fieldLabel ||
        theFieldDef.GetFieldDisplayName() == fieldLabel
      )
        return theFieldDef;
    }
    return null;
  }

  GetFieldLabelFromName(fieldName) {
    let fieldLabel = fieldName;
    const fieldDef = this.GetFieldDefByName(fieldName);
    if (fieldDef) {
      const label = fieldDef.GetFieldLabel();
      if (label != null && label != "") fieldLabel = label;
    }
    return fieldLabel;
  }

  GetFieldDefListByType(calcCode) {
    var fields = [];
    for (var i = 0; i < this.GetFieldCount(); i++) {
      var aFieldDef = this.GetIndexFieldDef(i);
      if (aFieldDef.GetCalcCode() === calcCode) fields.push(aFieldDef);
    }
    return fields;
  }

  BuildFieldIDIndex() {
    var IDIndex = new QGenericIndex();
    IDIndex.InitIndex();
    const caseSensitive = this.IsCaseSensitiveFields();
    var n = this.GetFieldCount();
    for (var i = 0; i < n; i++) {
      var fieldDef = this.fFieldList[i];
      var fieldID = fieldDef.GetFieldID();
      if (!caseSensitive) fieldID = fieldID.toLowerCase();
      IDIndex.AddToIndex(fieldDef, fieldID);
    }
    this.fFieldIDIndex = IDIndex;
    this._fieldDefCache = null;
  }

  GetFieldDefIndex(fieldDef) {
    var index = this.fFieldList.indexOf(fieldDef);
    return index;
  }

  GetFieldDefBySaveIndex(saveIndex) {
    var n = this.GetFieldCount();
    if (saveIndex[0] == "e") saveIndex = saveIndex.substring(1);

    saveIndex = parseInt(saveIndex);
    for (var i = 0; i < n; i++) {
      var fieldDef = this.fFieldList[i];
      var fieldSaveIndex = fieldDef.GetSaveIndex();
      if (saveIndex == fieldSaveIndex) return fieldDef;
    }

    return null;
  }

  /**
   * @param {string | null | undefined} fieldID
   * @returns {QObjDBFieldDef | null}
   */
  GetFieldDefByID(fieldID) {
    // Performance
    if (this.fFieldIDIndex == null) this.BuildFieldIDIndex();
    if (fieldID == null) return null;

    // Fast path: check cache first
    if (this._fieldDefCache == null) this._fieldDefCache = new Map();
    if (this._fieldDefCache.has(fieldID)) return this._fieldDefCache.get(fieldID);

    // Slow path: existing 3-attempt lookup
    const caseSensitive = this.IsCaseSensitiveFields();
    const useCarets = this.IsUsingFieldCarets();

    // Attempt 1: space-to-caret
    var modfieldID = useCarets ? fieldID.replaceAll(" ", "^") : fieldID;
    if (!caseSensitive) modfieldID = modfieldID.toLowerCase();
    var fieldDef = this.fFieldIDIndex.FindObject(modfieldID);

    // Attempt 2: remove spaces
    if (!fieldDef) {
      modfieldID = fieldID.replaceAll(" ", "");
      if (!caseSensitive) modfieldID = modfieldID.toLowerCase();
      fieldDef = this.fFieldIDIndex.FindObject(modfieldID);
    }

    // Attempt 3: direct lowercase (only useful in V2 case-insensitive mode)
    if (!fieldDef && !caseSensitive) {
      var lowerFieldID = fieldID.toLowerCase();
      fieldDef = this.fFieldIDIndex.FindObject(lowerFieldID);
    }

    this._fieldDefCache.set(fieldID, fieldDef);
    return fieldDef;
  }

  /**
   * @param {string | null | undefined} fieldKey
   * @returns {QObjDBFieldDef | null}
   */
  GetFieldDefByKeyWord(fieldKey) {
    // Performance
    if (this.fFieldIDIndex == null) this.BuildFieldIDIndex();
    return this.fFieldIDIndex.FindSimilarObject(fieldKey);
  }

  ForceUniqueID(fieldDef) {
    var i = 0;
    var startID = fieldDef.GetFieldID();
    var curID = startID;
    while (this.GetFieldDefByID(curID)) {
      curID = startID + (i += 1).toString();
    }
    fieldDef.SetFieldID(curID);
  }

  RemoveField(theField) {
    var index = this.fFieldList.indexOf(theField);
    if (index != -1) {
      this.fFieldList.splice(index, 1);
      this.ResetIndex();
      return true;
    }

    return false;
  }

  // Don't include non-persistent objects like clip-board or deleted objects
  GetPersistObjectCount() {
    return this.fObjMgr.GetPersistObjectCount();
  }

  GetRootObjs() {
    return this.fObjMgr.GetRootObjs();
  }

  GetObjectCount() {
    return this.fObjMgr.GetObjectCount();
  }

  GetObjectList() {
    return this.fObjMgr.GetObjectList();
  }

  GetIndexObject(index) {
    return this.fObjMgr.GetIndexObject(index);
  }

  AddFilter(aFilter) {
    return this.fObjMgr.AddFilter(aFilter);
  }

  AddHighlight(aHighlight) {
    return this.fObjMgr.AddHighlight(aHighlight);
  }

  AddNewObject() {
    var newObject = this.fObjMgr.AddNewObject();
    this.Journal(newObject, true);
    return newObject;
  }

  InsertObject(newObject) {
    this.fObjMgr.InsertObject(newObject);
    this.Journal(newObject, true);
    return newObject;
  }

  RetrieveObject(objectID) {
    return this.fObjMgr.RetrieveObject(objectID);
  }

  DeleteObject(theObj) {
    return this.fObjMgr.DeleteObject(theObj);
  }

  FindObject(fieldDef, fieldValue, persistOnly) {
    return this.fObjMgr.FindObject(fieldDef, fieldValue, persistOnly);
  }

  BuildObjectMap(fieldDef, persistOnly) {
    return this.fObjMgr.BuildObjectMap(fieldDef, persistOnly);
  }

  FindMatchingObjectsByPredicate(predicate) {
    return this.fObjMgr.FindMatchingObjectsByPredicate(predicate);
  }

  UpdateNonPersistList(dbObj, persist) {
    this.fObjMgr.UpdateNonPersistList(dbObj, persist);
  }

  GetFilteredObjectCount() {
    return this.fObjMgr.GetFilteredObjectCount();
  }

  GetAuxCount() {
    var auxCount = 0;
    var objMgr = this.GetObjMgr();
    var dbObjList = objMgr.GetObjectList();
    dbObjList.forEach(function (anObj) {
      if (anObj.IsAux()) {
        auxCount += 1;
      }
    });
    return auxCount;
  }

  LoadFromJSON(json) {
    var className = json["className"];
    var isOverrideClass = className === enumObjClassNames.override;
    var isPositionClass = className === enumObjClassNames.position;
    this.InitClass(className, true, Globals._wsMgr, isOverrideClass);

    //this.fDefUID = json['fieldUID'];	//TOOD: Do we need to increment this here?

    var fieldList = json["fieldDefs"];
    var n = fieldList.length;
    for (var i = 0; i < n; i++) {
      var c = fieldList[i];
      if (this.CheckForSystemFieldDuplication(c["fieldName"], className)) continue;
      var aField = new QObjDBFieldDef();
      aField.SetParentClass(this);
      aField.LoadFromJSON(c);
      var fieldID = aField.GetFieldID().toLowerCase();
      const isEmployeeCountField = ["headcount", "spanofcontrol", "subordinates", "peers"].includes(
        fieldID
      );
      const normalizedID = fieldID.replaceAll("^", " ");
      const isPositionCountField = kPositionCountFields
        .map(id => id.toLowerCase())
        .includes(normalizedID);
      if (isEmployeeCountField || isPositionCountField || fieldID === "level") {
        // For position class, skip employee-count calc fields so they don't get a save
        // index and aren't written back to position records on next save.
        // Position-count fields belong on the position class — do NOT skip them.
        if (isPositionClass && isEmployeeCountField) continue;
        // GetFieldName() returns the display name with spaces (e.g. "Position Count"), which
        // GetFieldDefByID can resolve via its remove-spaces lookup attempt ("positioncount").
        var theField =
          this.GetFieldDefByID(aField.GetFieldID()) || this.GetFieldDefByID(aField.GetFieldName());
        if (!theField) continue;
        // Preserve the calc code — only update the save index so stored values can be
        // read back. The live calc (DoCalc) is used for these fields; the save index
        // allows the cached/stored value to round-trip through JSON without data loss.
        theField.SetSaveIndex(aField.GetSaveIndex());
      } else {
        // If a field with this ID was already registered by AddCalcDefs, just update its
        // save index so object data can still find it by index — don't add a duplicate.
        var existingCalcField = this.GetFieldDefByID(aField.GetFieldID());
        if (existingCalcField) {
          existingCalcField.SetSaveIndex(aField.GetSaveIndex());
        } else {
          this.AddField(aField);
        }
      }
    }
  }

  OutputToJSON(stream) {
    var classObj = {};
    var className = this.GetClassName();
    classObj["className"] = className;
    classObj["fieldUID"] = this.fDefUID;
    classObj["fieldDefs"] = [];
    var saveIndex = 0;
    for (var i in this.fFieldList) {
      var fieldDef = this.GetIndexFieldDef(i);
      // Don't output calculation fields.
      var calcCode = fieldDef.GetCalcCode();
      if (
        calcCode == null ||
        calcCode == enumCalcCode.customcalc ||
        calcCode == enumCalcCode.static ||
        calcCode == enumCalcCode.aggregatedField
      ) {
        fieldDef.SetSaveIndex(saveIndex);
        classObj["fieldDefs"].push(fieldDef.OutputToJSON(stream));
        saveIndex++;
      } else if (fieldDef.GetFieldID().indexOf("_") > -1) {
        //TODO: What happens with organic fields coming in with underscores...?
        //console.log('not dumping ' + fieldDef.GetFieldLabel())
      }
    }
    return classObj;
  }

  OutputSubElements(stream) {
    var className = this.GetClassName();

    this.OutputSimpleElementF("classname", className, stream);
    this.OutputSimpleElementF("fieldUID", this.fDefUID, stream);
  }

  OutputChildElements(stream) {
    var saveIndex = 0;
    for (var i in this.fFieldList) {
      var fieldDef = this.GetIndexFieldDef(i);
      // Don't output calculation fields.
      var calcCode = fieldDef.GetCalcCode();
      if (
        calcCode == null ||
        calcCode == enumCalcCode.customcalc ||
        calcCode == enumCalcCode.static ||
        calcCode == enumCalcCode.aggregatedField
      ) {
        fieldDef.SetSaveIndex(saveIndex);
        fieldDef.OutputToXML(stream);
        saveIndex++;
      } else if (fieldDef.GetFieldID().indexOf("_") > -1) {
        //console.log('not dumping ' + fieldDef.GetFieldLabel())
      }
    }
  }

  LoadSubElements(objTree) {
    var className = objTree.GetChildByName("classname");
    this.InitClass(className.GetValue(), true, Globals._wsMgr);
    /*
    var UIDInfo = objTree.GetChildByName("fieldUID");
    if (UIDInfo != null)
        this.fDefUID = parseInt(UIDInfo.GetValue());
    else
        this.fDefUID = 0;
	*/
  }

  LoadChildElements(objTree) {
    var fieldObjName = "qfield";
    var childList = objTree.GetChildOfTypeList(fieldObjName);
    var n = childList.length;
    var isPositionClass = this.GetClassName() === enumObjClassNames.position;
    for (var i = 0; i < n; i++) {
      var c = childList[i];
      //var c = objTree.GetIndexChildOfType(fieldObjName, i);
      var aField = new QObjDBFieldDef();
      aField.SetParentClass(this);
      aField.LoadFromXML(c);
      var fieldID = aField.GetFieldID().toLowerCase();
      if (
        fieldID === "headcount" ||
        fieldID === "spanofcontrol" ||
        fieldID === "subordinates" ||
        fieldID === "level" ||
        fieldID === "peers"
      ) {
        // For position class, skip employee-count calc fields.
        if (isPositionClass && fieldID !== "level") continue;
        var theField = this.GetFieldDefByID(aField.GetFieldID());
        if (!theField) continue;
        // Preserve the calc code — only update the save index so stored values can be
        // read back. The live calc (DoCalc) is used for these fields.
        theField.SetSaveIndex(aField.GetSaveIndex());
      } else {
        var existingCalcField = this.GetFieldDefByID(aField.GetFieldID());
        if (existingCalcField) {
          existingCalcField.SetSaveIndex(aField.GetSaveIndex());
        } else {
          this.AddField(aField);
        }
      }
    }

    // Backward compatibility
    var fieldDef = this.GetFieldDefByID("_objInfo");
    if (fieldDef != null) fieldDef.SetInternalOnly(true);

    var fieldDef = this.GetFieldDefByID("treeID");
    if (fieldDef != null) fieldDef.SetInternalOnly(true);

    var fieldDef = this.GetFieldDefByID("scFlag");
    if (fieldDef != null) fieldDef.SetInternalOnly(true);

    var fieldDef = this.GetFieldDefByID("Orphan");
    if (fieldDef == null) this.AddOrphanField();

    fieldDef = this.GetFieldDefByID("TopOfSubChart");
    if (fieldDef == null) this.AddTopSubChartField();

    fieldDef = this.GetFieldDefByID("Locked");
    if (fieldDef == null) this.AddLockedField();

    fieldDef = this.GetFieldDefByID("Persist");
    if (fieldDef == null) this.AddPersistField();
  }

  PurgeData() {
    this.fObjMgr.PurgeData();
  }

  AnyObjectLocked() {
    return this.fObjMgr.AnyObjectLocked();
  }

  GetFilterMgr() {
    return this.fObjMgr.GetFilterMgr();
  }

  GetHighlightMgr() {
    return this.fObjMgr.GetHighlightMgr();
  }

  GetMgrActionMgr() {
    return this.fObjMgr.GetMgrActionMgr();
  }

  GenerateUniqueRecordID(additionalObjects = []) {
    var objMgr = this.GetObjMgr();
    var clonedObjectList = [...objMgr.GetObjectList(), ...additionalObjects];
    var addList = clonedObjectList.map(function (objDBObject) {
      return objDBObject.GetParentClassName() === enumObjClassNames.override
        ? objDBObject.SmartGetFieldValue("RefID")
        : objDBObject.GetObjectRecordID();
    });
    var curCount = 1;
    var inUse = true;
    var testName = "NEW_000001";

    while (inUse) {
      inUse = false;
      curCount += 1;
      var anIdx = addList.indexOf(testName);
      if (anIdx > -1) {
        inUse = true;
        testName = "NEW_" + curCount.pad(6);
      }
    }

    return testName;
  }

  CheckForSystemFieldDuplication(fieldName, className) {
    const existingField = this.GetFieldDefByName(fieldName);
    if (existingField == null) return false;

    let duplicatedFields;

    switch (className) {
      case enumObjClassNames.box:
        duplicatedFields = [
          kEmployeePositionID,
          kNameID,
          kEmployeeID,
          kTitleID,
          kEmployeeLocation,
          kEmployeeSalary,
          kEmployeePay,
          kEmployeeCostCenter
        ];
        break;
      case enumObjClassNames.position:
        duplicatedFields = kPositionFieldIDs;
        break;
      default:
        return false;
    }

    return this.IsUsingFieldCarets()
      ? duplicatedFields.map(id => id.replaceAll(" ", "^")).includes(existingField.GetFieldID())
      : duplicatedFields.includes(existingField.GetFieldID());
  }
}
